import { useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface TabItem {
  key: string
  label: ReactNode
  /** Licznik w nawiasie, jak „Dokumenty (64)". */
  count?: number | null
  disabled?: boolean
}

export interface TabsProps {
  items: TabItem[]
  /** Kontrolowane. Gdy pominięte — komponent trzyma stan sam. */
  value?: string
  defaultValue?: string
  onChange?: (key: string) => void
  /** 'underline' — karta sprawy, 'pills' — filtry typu „Wszystkie (41)". */
  variant?: 'underline' | 'pills'
  className?: string
  right?: ReactNode
}

/** Zakładki z podkreśleniem aktywnej (karta sprawy) lub pigułkami (skrzynka). */
export function Tabs({ items, value, defaultValue, onChange, variant = 'underline', className, right }: TabsProps) {
  const [internal, setInternal] = useState(defaultValue ?? items[0]?.key ?? '')
  const active = value ?? internal

  const select = (key: string) => {
    if (value === undefined) setInternal(key)
    onChange?.(key)
  }

  if (variant === 'pills') {
    return (
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        {items.map((item) => {
          const isActive = item.key === active
          return (
            <button
              key={item.key}
              type="button"
              disabled={item.disabled}
              onClick={() => select(item.key)}
              className={cn(
                'h-[30px] rounded border px-3 text-base transition-colors duration-fast disabled:opacity-50',
                isActive
                  ? 'border-navy-900 bg-navy-900 font-medium text-white'
                  : 'border-line bg-card text-muted hover:text-ink',
              )}
            >
              {item.label}
              {item.count !== undefined && item.count !== null ? ` (${item.count})` : ''}
            </button>
          )
        })}
        {right ? <div className="ml-auto">{right}</div> : null}
      </div>
    )
  }

  return (
    <div className={cn('flex items-end gap-6 border-b border-line-soft', className)} role="tablist">
      {items.map((item) => {
        const isActive = item.key === active
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={item.disabled}
            onClick={() => select(item.key)}
            className={cn(
              '-mb-px border-b-2 pb-2.5 pt-1 text-base transition-colors duration-fast disabled:opacity-50',
              isActive
                ? 'border-navy-900 font-semibold text-ink'
                : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {item.label}
            {item.count !== undefined && item.count !== null ? (
              <span className={cn('ml-1', isActive ? 'text-ink' : 'text-muted')}>({item.count})</span>
            ) : null}
          </button>
        )
      })}
      {right ? <div className="ml-auto pb-2">{right}</div> : null}
    </div>
  )
}
